'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface Marker {
  id: string;
  x: number;
  y: number;
  label: string;
  description: string;
}

const SAMPLE_DATA = {
  title: '1959 Gibson Les Paul Standard',
  year: '1959',
  category: 'Vintage Guitar',
  description: 'An exceptionally rare 1959 Gibson Les Paul Standard in original sunburst finish. This instrument represents the pinnacle of electric guitar design, with its lightweight mahogany body, figured maple top, and PAF humbucking pickups. The guitar has been professionally maintained and retains all original components.',
  collectorNotes: 'Acquired from the estate of a session musician who played on numerous Motown recordings in the 1960s. The wear patterns tell the story of countless studio sessions and live performances.',
  signedBy: ['Eric Clapton', 'Jimmy Page'],
  images: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_19aa54d73-1765753346482.png", alt: 'Front view of 1959 Les Paul' },
  { url: "https://images.unsplash.com/photo-1702859963843-246255e6a65f", alt: 'Headstock detail' },
  { url: "https://images.unsplash.com/photo-1695506976351-d7aa68bb7d67", alt: 'Back view showing grain' }],

  authenticity: {
    authenticated: true,
    inPerson: true,
    signed: true,
    certified: true,
    certifiedBy: 'Gruhn Guitars',
    certificateNumber: 'GG-LP-1959-0847',
    certificationDate: 'March 15, 2023'
  },
  markers: [
  { id: 'm1', x: 45, y: 25, label: '🎸', description: 'Original PAF pickup - Patent Applied For humbucker' },
  { id: 'm2', x: 50, y: 15, label: '🔧', description: 'Kluson tuners - All original from 1959' },
  { id: 'm3', x: 48, y: 45, label: '✨', description: 'Flame maple top - AAA grade figuring' },
  { id: 'm4', x: 52, y: 60, label: '🎚️', description: 'Original potentiometers dated 1959' },
  { id: 'm5', x: 46, y: 75, label: '🔒', description: 'Trapeze tailpiece - Factory original' },
  { id: 'm6', x: 55, y: 35, label: '📝', description: 'Serial number stamped in cavity' }]

};

export default function VintageScrapbookViewer() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMarkers, setShowMarkers] = useState(false);
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#faf6f0] relative overflow-hidden">
      {/* Textured Background */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4a574' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      {/* Header with Handwritten Feel */}
      <header className="relative z-10 pt-12 pb-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block px-6 py-2 bg-[#d4a574] text-white rounded-full text-sm font-medium mb-4 rotate-[-1deg] shadow-md">
            {SAMPLE_DATA.category}
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-[#5a3e2b] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            {SAMPLE_DATA.title}
          </h1>
          <div className="text-2xl text-[#8b6f47] font-light" style={{ fontFamily: 'Georgia, serif' }}>
            Est. {SAMPLE_DATA.year}
          </div>
        </div>
      </header>

      {/* Main Content - Scattered Scrapbook Layout */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Side - Polaroid Images */}
          <div className="lg:col-span-5 space-y-8">
            {/* Main Polaroid */}
            <div className="relative transform rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
              <div className="bg-white p-4 pb-16 shadow-2xl rounded-sm">
                <div className="aspect-[3/4] relative bg-gray-200 overflow-hidden">
                  <Image
                    src={SAMPLE_DATA.images[currentImageIndex].url}
                    alt={SAMPLE_DATA.images[currentImageIndex].alt}
                    fill
                    className="object-cover" />
                  
                  
                  {/* Vintage Map Pin Markers */}
                  {showMarkers && SAMPLE_DATA.markers.map((marker, idx) =>
                  <div
                    key={marker.id}
                    className="absolute transform -translate-x-1/2 -translate-y-full cursor-pointer transition-all duration-300 hover:scale-125 z-10"
                    style={{
                      left: `${marker.x}%`,
                      top: `${marker.y}%`,
                      animationDelay: `${idx * 0.1}s`
                    }}
                    onMouseEnter={() => setHoveredMarker(marker.id)}
                    onMouseLeave={() => setHoveredMarker(null)}>
                    
                      {/* Vintage Pin */}
                      <div className="relative">
                        <div className="w-8 h-8 bg-[#c44536] rounded-full border-2 border-[#8b3226] shadow-lg flex items-center justify-center">
                          <span className="text-base">{marker.label}</span>
                        </div>
                        <div className="w-1 h-4 bg-[#8b3226] mx-auto"></div>
                      </div>
                      
                      {/* Vintage Tooltip */}
                      {hoveredMarker === marker.id &&
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-[#f4e4d7] border-2 border-[#d4a574] rounded-lg p-3 shadow-xl">
                          <div className="text-sm font-medium text-[#5a3e2b] mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                            {marker.label} {marker.description.split('-')[0]}
                          </div>
                          <div className="text-xs text-[#8b6f47]">{marker.description.split('-')[1]}</div>
                        </div>
                    }
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center text-[#8b6f47] italic" style={{ fontFamily: 'Georgia, serif' }}>
                  {SAMPLE_DATA.images[currentImageIndex].alt}
                </div>
              </div>
              {/* Tape Effect */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-20 h-8 bg-[#ffd89b] opacity-60 rotate-[-5deg]"></div>
            </div>

            {/* Markers Toggle Button - Vintage Style */}
            <div className="transform rotate-[1deg]">
              <button
                onClick={() => setShowMarkers(!showMarkers)}
                className="w-full py-4 px-6 bg-[#c44536] hover:bg-[#a33829] text-white font-medium rounded-lg transition-all duration-300 shadow-lg border-2 border-[#8b3226]"
                style={{ fontFamily: 'Georgia, serif' }}>
                
                {showMarkers ? '✓ Pins Visible' : '📍 Show Detail Pins'}
              </button>
            </div>

            {/* Thumbnail Polaroids */}
            <div className="flex gap-4 justify-center">
              {SAMPLE_DATA.images.map((img, idx) =>
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`transform transition-all duration-300 ${
                currentImageIndex === idx ?
                'scale-110 rotate-0' : 'scale-90 opacity-70 hover:opacity-100 hover:scale-100'}`
                }
                style={{ rotate: `${(idx - 1) * 3}deg` }}>
                
                  <div className="bg-white p-2 pb-6 shadow-lg rounded-sm w-20">
                    <Image
                    src={img.url}
                    alt={img.alt}
                    width={80}
                    height={100}
                    className="w-full h-20 object-cover" />
                  
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Right Side - Scrapbook Content */}
          <div className="lg:col-span-7 space-y-6">
            {/* Wax Seal Authenticity Badges */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-lg shadow-lg border-2 border-[#d4a574] transform rotate-[0.5deg]">
              <div className="text-sm font-bold text-[#5a3e2b] uppercase tracking-wider mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                🎖️ Authenticity Seals
              </div>
              <div className="flex flex-wrap gap-3">
                {SAMPLE_DATA.authenticity.authenticated &&
                <div className="px-4 py-2 bg-[#10b981] text-white rounded-full text-sm font-medium shadow-md border-2 border-[#059669]">
                    ✓ Authenticated
                  </div>
                }
                {SAMPLE_DATA.authenticity.inPerson &&
                <div className="px-4 py-2 bg-[#3b82f6] text-white rounded-full text-sm font-medium shadow-md border-2 border-[#2563eb]">
                    ✓ In-Person
                  </div>
                }
                {SAMPLE_DATA.authenticity.signed &&
                <div className="px-4 py-2 bg-[#8b5cf6] text-white rounded-full text-sm font-medium shadow-md border-2 border-[#7c3aed]">
                    ✓ Signed
                  </div>
                }
                {SAMPLE_DATA.authenticity.certified &&
                <div className="px-4 py-2 bg-[#f59e0b] text-white rounded-full text-sm font-medium shadow-md border-2 border-[#d97706]">
                    ✓ Certified
                  </div>
                }
              </div>
              
              {/* Certificate Details */}
              {SAMPLE_DATA.authenticity.certified &&
              <div className="mt-4 pt-4 border-t-2 border-dashed border-[#d4a574]">
                  <div className="text-xs text-[#8b6f47] space-y-1" style={{ fontFamily: 'Georgia, serif' }}>
                    <div><strong>Certified by:</strong> {SAMPLE_DATA.authenticity.certifiedBy}</div>
                    <div><strong>Certificate #:</strong> {SAMPLE_DATA.authenticity.certificateNumber}</div>
                    <div><strong>Date:</strong> {SAMPLE_DATA.authenticity.certificationDate}</div>
                  </div>
                </div>
              }
            </div>

            {/* About Card - Journal Entry Style */}
            <div className="bg-[#fffef9] p-8 rounded-lg shadow-lg border-l-4 border-[#d4a574] transform rotate-[-0.5deg]">
              <h2 className="text-2xl font-bold text-[#5a3e2b] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                📖 The Story
              </h2>
              <p className="text-[#6b5744] leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontSize: '15px' }}>
                {SAMPLE_DATA.description}
              </p>
            </div>

            {/* Signatures - Handwritten Style */}
            {SAMPLE_DATA.signedBy.length > 0 &&
            <div className="bg-gradient-to-br from-[#f4e4d7] to-[#e8d5c4] p-8 rounded-lg shadow-lg border-2 border-[#d4a574] transform rotate-[0.5deg]">
                <div className="text-sm font-bold text-[#8b6f47] uppercase tracking-wider mb-4">
                  ✍️ Signed By
                </div>
                <div className="space-y-3">
                  {SAMPLE_DATA.signedBy.map((name, idx) =>
                <div
                  key={idx}
                  className="text-3xl text-[#5a3e2b] transform"
                  style={{
                    fontFamily: 'Brush Script MT, cursive',
                    rotate: `${(idx % 2 === 0 ? -1 : 1) * 2}deg`
                  }}>
                  
                      {name}
                    </div>
                )}
                </div>
              </div>
            }

            {/* Collector Notes - Aged Paper */}
            <div className="bg-[#f9f3e8] p-8 rounded-lg shadow-lg border border-[#d4a574] transform rotate-[-0.3deg]">
              <h2 className="text-xl font-bold text-[#5a3e2b] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                📝 Collector's Journal
              </h2>
              <p className="text-[#6b5744] leading-relaxed italic" style={{ fontFamily: 'Georgia, serif', fontSize: '15px' }}>
                “{SAMPLE_DATA.collectorNotes}”
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>);

}